import ChatArchiveSelection, {ChatArchiveCheckbox, useChatArchive, hasArchiveMedia} from './ChatArchiveSelection';
import { useChatMessageQueue } from '../../hooks/useChatMessageQueue';
import ChatQueueStatus from './ChatQueueStatus';
import { upsertMessage } from '../../utils/chatSync';
import { useChatCatchUp } from '../../hooks/useChatCatchUp';
import { personName } from '../../personName';
import { orderedMessages } from '../../utils/messageOrder';
import { newClientUid } from '../../utils/chatSync';
import { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import IconButton from '@mui/joy/IconButton';
import CircularProgress from '@mui/joy/CircularProgress';

import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';

import ChatBubble from './ChatBubble';
import MessagesPaneHeader from './MessagesPaneHeader';
import AvatarWithStatus from './AvatarWithStatus';
import MessageInput from './MessageInput';
import ReadReceiptsBar from './ReadReceiptsBar';
import PinnedBar from './PinnedBar';
import ModalPinnedList from '../Modals/ModalPinnedList';
import MessageContextMenu from './MessageContextMenu';
import MessageViewsModal from './MessageViewsModal';

import { LayoutContext } from '../../context';
import {
  API_URL, APIGetChatMessagesById, APISendMessage, APISendVoiceMessage, createWebSocketConnection,
  APIDeleteMessage, APIEditMessage, APIToggleMessageReaction,
  APIGetMessageViews, APIMarkChatReadAll,
  APIToggleMessagePin, APIGetPinnedMessages,
} from '../../api';
import { track } from '../../utils/tracker';
import { usePresence } from '../../hooks/usePresence';
import {
  CHAT, MOTION, canvasBackground, thinScrollbar, dayKey, dayLabel,
  dateChip, dayChipWrap, unreadDivider, iconColor,
} from './chatTheme';

// Сообщения одного автора, отправленные подряд в пределах этого окна,
// склеиваются в серию: аватар и имя рисуются один раз.
const GROUP_WINDOW_MS = 5 * 60 * 1000;

// Сколько ждём, прежде чем отправить серверу пачку «вот это я увидел».
// Без задержки быстрая прокрутка выдаёт запрос на каждое сообщение.
const READ_FLUSH_MS = 400;

let tmpSeq = 0;
const nextTmpId = () => `tmp-${Date.now()}-${(tmpSeq += 1)}`;

const isTmp = (id) => String(id).startsWith('tmp-');

/**
 * Лента сообщений.
 *
 * Разделители дат снова липкие — но правильно. Каждый разделитель липнет
 * внутри СВОЕЙ секции дня, поэтому, доехав до конца своего дня, он
 * выталкивается следующим. В промежуточном варианте липли все чипы сразу
 * (они наслаивались и темнели), потом чип сделали один плавающий — и он
 * дублировал разделитель в ленте и появлялся невпопад. Секции решают оба
 * случая штатными средствами CSS, без слушателей прокрутки.
 *
 * Прочтения устроены как в мессенджерах:
 *  • чат открывается на границе непрочитанного, а не всегда в самом низу;
 *  • граница отмечена разделителем и не уезжает, пока чат открыт;
 *  • сообщение считается прочитанным, когда оно реально показалось на
 *    экране, а не когда его загрузил браузер;
 *  • кнопка «вниз» показывает, сколько осталось, и гасит всё разом.
 */
export default function MessagesPane({ chat, chats, setChats, setSearchParams, searchParams, setSelectedChat }) {
  const { account, tokenEmployee, markAsRead } = useContext(LayoutContext);

  const [messages, commitMessages] = useState([]);
  const setMessages = useCallback(value => commitMessages(prev =>
    orderedMessages(typeof value === 'function' ? value(prev) : value)), []);
  const sessionKey = `${chat?.chat_id || ''}:${tokenEmployee || ''}`;
  const activeSessionRef = useRef(sessionKey);
  activeSessionRef.current = sessionKey;
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [textAreaValue, setTextAreaValue] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [showJump, setShowJump] = useState(false);
  const [menu, setMenu] = useState(null);       // { anchor, message }
  const [editing, setEditing] = useState(null); // сообщение в режиме правки
  const [actionError, setActionError] = useState('');
  const [attachmentReset, resetAttachmentInput] = useState(0);

  const attachmentQueue = useChatMessageQueue({
    sessionKey,
    send: fd => APISendMessage(chat.chat_id, fd, tokenEmployee),
    onMessage: sent => {
      setMessages(prev => upsertMessage(prev, sent));
      setChats(prev => prev.map(item => item?.chat_id === chat.chat_id
        ? { ...item, last_message: lastMessageOf(sent) } : item));
      scrollToBottom();
    },
    onDone: () => {
      setTextAreaValue(''); setAttachments([]); setReplyTo(null);
      resetAttachmentInput(n => n + 1);
    },
  });
  useEffect(() => {
    const job = attachmentQueue.job;
    setAttachments(job?.files || []);
    setTextAreaValue(job?.content || '');
    setReplyTo(job?.replyTo || null);
    setEditing(null);
  // Restore only when changing chat/account; progress must not overwrite input.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionKey]);

  // Граница непрочитанного. Запоминается при открытии чата и НЕ двигается,
  // пока чат открыт: иначе разделитель убегал бы вниз по мере чтения.
  const [firstUnreadId, setFirstUnreadId] = useState(null);

  // Просмотры сообщения (кто прочитал, кто отреагировал)
  const [viewsData, setViewsData] = useState(null);
  const [viewsLoading, setViewsLoading] = useState(false);
  const [viewsOpen, setViewsOpen] = useState(false);

  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const socketRef = useRef(null);
  const unreadDividerRef = useRef(null);

  // Наблюдатель за показом сообщений на экране
  const ioRef = useRef(null);
  const msgNodes = useRef(new Map());
  const pendingReadRef = useRef(new Set());
  const readTimerRef = useRef(null);
  const [visTick, setVisTick] = useState(0);

  const [wsConnectionStatus, setWsConnectionStatus] = useState(false);
  // Код осознанного отказа сервера; null — обычная работа/переподключение.
  const [wsFatalCode, setWsFatalCode] = useState(null);
  const mediaArchive = useChatArchive({apiURL: API_URL, chatId: chat?.chat_id, token: tokenEmployee, messages,
    enabled: Boolean(chat?.chat_id && tokenEmployee && !wsFatalCode)});

  // Онлайн-статусы участников: ручка при открытии + события по сокету.
  const {
    presence, reloadPresence, applyPresenceEvent,
  } = usePresence(chat?.chat_id, tokenEmployee);
  const [initialLoad, setInitialLoad] = useState(true);
  const [prevScrollHeight, setPrevScrollHeight] = useState(0);
  const [chatParticipants, setChatParticipants] = useState([]);
  const [chatMeta, setChatMeta] = useState(null);
  const currentChat = chatMeta?.sessionKey === sessionKey ? { ...chat, ...chatMeta.chat } : chat;
  // Счётчик событий о прочтении: полоска «кто прочитал» перезапрашивает
  // список, когда он меняется. Держим числом, а не самим списком, — состав
  // читателей знает сервер, и спорить с ним незачем.
  const [readTick, setReadTick] = useState(0);
  // Закреплённые: полный список для полоски и модалки + подсветка после
  // перехода + флажок «догружаем ленту, чтобы доскроллить».
  const [pinned, setPinned] = useState([]);
  const [pinnedIndex, setPinnedIndex] = useState(0);
  const [pinnedListOpen, setPinnedListOpen] = useState(false);
  const [flashId, setFlashId] = useState(null);
  const [jumping, setJumping] = useState(false);
  const [pinError, setPinError] = useState(null);

  useChatCatchUp({ chatId: chat?.chat_id, token: tokenEmployee, ready: !initialLoad,
    messages, setMessages, connected: wsConnectionStatus });

  const isGroup = currentChat?.type_chat !== 'direct' && !currentChat?.is_dm;

  const isMine = m => Boolean(account?.id != null && m?.sender?.id != null
    && m.sender.kind !== 'client' && String(account.id) === String(m.sender.id));

  // Какие сообщения ещё ждут отметки «прочитано». Свои и временные — никогда.
  const needsRead = (m) => Boolean(
    m && m.is_read === false && !isMine(m) && !isTmp(m.id),
  );

  const mergeArrays = (arr1, arr2) => {
    const merged = new Map();
    [...arr1, ...arr2].forEach((item) => merged.set(item.id, item));
    return Array.from(merged.values());
  };

  // ── Отметка прочитанного ───────────────────────────────────────────
  const flushRead = () => {
    if (document.visibilityState !== 'visible') return;
    const requestedSession = sessionKey;
    const ids = Array.from(pendingReadRef.current);
    pendingReadRef.current.clear();
    if (ids.length === 0 || !chat?.chat_id) return;

    const idSet = new Set(ids);
    setMessages((prev) => prev.map((m) => (idSet.has(m.id) ? { ...m, is_read: true } : m)));
    // markAsRead заодно гасит бейдж чата в списке и пересчитывает общий счётчик
    Promise.resolve(markAsRead?.(chat.chat_id, ids)).catch(() => {
      if (activeSessionRef.current !== requestedSession) return;
      setMessages(prev => prev.map(m => idSet.has(m.id) ? { ...m, is_read: false } : m));
      setTimeout(() => {
        if (activeSessionRef.current === requestedSession) setVisTick(t => t + 1);
      }, 2000);
    });
  };

  const queueRead = (id) => {
    pendingReadRef.current.add(id);
    if (readTimerRef.current) clearTimeout(readTimerRef.current);
    readTimerRef.current = setTimeout(flushRead, READ_FLUSH_MS);
  };

  // Вкладку свернули — сообщения «на экране» на самом деле никто не видит.
  // Вернулись — пересобираем наблюдение и досчитываем то, что видно сейчас.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') setVisTick((t) => t + 1);
    };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, []);

  useEffect(() => {
    const root = messagesContainerRef.current;
    if (!root || !chat?.chat_id) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (document.visibilityState !== 'visible') return;
      entries.forEach((e) => {
        if (!e.isIntersecting || e.intersectionRatio < 0.6) return;
        const raw = e.target.getAttribute('data-mid');
        if (!raw) return;
        const id = Number(raw);
        if (Number.isNaN(id)) return;
        queueRead(id);
        io.unobserve(e.target);
      });
    }, { root, threshold: 0.6 });
    ioRef.current = io;
    return () => {
      io.disconnect();
      ioRef.current = null;
      if (readTimerRef.current) clearTimeout(readTimerRef.current);
    };
  }, [chat?.chat_id]);

  // Пересобираем наблюдение при каждом изменении ленты. disconnect + observe
  // заново — так те сообщения, что видны прямо сейчас, снова дают событие
  // (IntersectionObserver сообщает о состоянии сразу после observe).
  useEffect(() => {
    const io = ioRef.current;
    if (!io) return;
    io.disconnect();
    messages.forEach((m) => {
      if (!needsRead(m)) return;
      const node = msgNodes.current.get(m.id);
      if (node) io.observe(node);
    });
  }, [messages, visTick, account?.id]);

  const bindMsgNode = (id) => (node) => {
    if (node) msgNodes.current.set(id, node);
    else msgNodes.current.delete(id);
  };

  // ── Закреплённые ───────────────────────────────────────────────────
  const reloadPinned = useCallback(async () => {
    if (!chat?.chat_id) { setPinned([]); return; }
    const res = await APIGetPinnedMessages(chat.chat_id, tokenEmployee);
    if (res?.status === 'ok') setPinned(res.pinned || []);
  }, [chat?.chat_id, tokenEmployee]);

  useEffect(() => {
    setPinned([]);
    setPinnedIndex(0);
    reloadPinned();
  }, [reloadPinned]);

  // Плашка отказа гаснет сама — держать её до клика незачем.
  useEffect(() => {
    if (!pinError) return undefined;
    const t = setTimeout(() => setPinError(null), 4000);
    return () => clearTimeout(t);
  }, [pinError]);

  // Полоска показывает по одному закреплённому. Нажатие листает дальше по
  // кругу — как в телеграме: так до нужного добираешься, не открывая список.
  const activePinned = pinned.length > 0
    ? pinned[Math.min(pinnedIndex, pinned.length - 1)]
    : null;

  const handleTogglePinMessage = async (m) => {
    if (!m?.id || isTmp(m.id)) return;
    const next = !m.is_pinned;
    // Показываем сразу: ответ сервера ничего не добавляет, а задержка видна.
    setMessages((prev) => prev.map((x) => (
      x?.id === m.id ? { ...x, is_pinned: next } : x
    )));
    const res = await APIToggleMessagePin(chat.chat_id, m.id, next, tokenEmployee);
    if (res?.status !== 'ok') {
      // Откатываем и объясняем: чаще всего это упор в лимит закреплённых.
      setMessages((prev) => prev.map((x) => (
        x?.id === m.id ? { ...x, is_pinned: !next } : x
      )));
      setPinError(res?.message || 'Не удалось изменить закрепление');
      return;
    }
    setPinnedIndex(0);
    reloadPinned();
  };

  // ── Переход к сообщению ────────────────────────────────────────────
  // Зеркало ленты в ref: в цикле догрузки читать состояние нельзя — оно
  // обновится только после рендера, а решение «нашли или грузим дальше»
  // нужно прямо в цикле.
  const messagesRef = useRef([]);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const scrollToLoaded = (id) => {
    const node = msgNodes.current.get(id);
    if (!node) return false;
    node.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setFlashId(id);
    // Подсветка гаснет сама: постоянная рамка на сообщении выглядела бы
    // как его новое состояние, а это просто «вот оно, смотри сюда».
    setTimeout(() => setFlashId((cur) => (cur === id ? null : cur)), 1700);
    return true;
  };

  const jumpToMessage = async (id) => {
    if (!id || !chat?.chat_id) return;
    if (scrollToLoaded(id)) return;

    // Сообщение старше загруженного окна. Просим у сервера окно ленты,
    // ЗАКАНЧИВАЮЩЕЕСЯ на нём (anchor=message:<id>), — одним запросом.
    //
    // Догрузка страницами «пока не наткнёмся» тут не годится: на переписке
    // в тысячу сообщений это десяток запросов подряд и тысяча узлов в DOM
    // ради одного прыжка, а на более длинной — упор в предел вообще без
    // результата, причём молча. Окно по якорю сервер считает сам и честно
    // говорит флагом anchor_hit, дотянулся он или нет.
    setJumping(true);
    try {
      const data = await APIGetChatMessagesById(
        chat.chat_id, tokenEmployee, 30, 0,
        { anchor: `message:${id}`, markRead: false },
      );
      if (data?.anchor_hit === false) {
        // Не дотянулись. Ленту НЕ трогаем: при промахе сервер отдаёт самую
        // свежую страницу, и подмена ею сбросила бы человека вниз — он
        // хотел перейти к сообщению, а не потерять место, где читал.
        setPinError('Сообщение слишком далеко в переписке — откройте его поиском');
        return;
      }

      const list = data?.messages?.reverse() || [];
      if (list.length > 0) {
        messagesRef.current = list;
        setMessages(list);
        setHasMore(data?.has_more ?? false);
        if (data?.first_unread_id !== undefined) setFirstUnreadId(data.first_unread_id ?? null);
      }

      // Ждём кадр отрисовки: до него узла в msgNodes ещё нет.
      await new Promise((r) => setTimeout(r, 80));
      if (!scrollToLoaded(id)) {
        setPinError('Не удалось найти это сообщение — возможно, его удалили');
      }
    } catch (e) {
      console.error('Не удалось перейти к сообщению:', e);
      setPinError('Не удалось перейти к сообщению');
    } finally {
      setJumping(false);
    }
  };

  // ── Загрузка ───────────────────────────────────────────────────────
  const loadMessages = async (offset = 0, limit = 30) => {
    if (!chat?.chat_id || loading) return;
    const requestedSession = sessionKey;
    setLoading(true);
    try {
      const data = await APIGetChatMessagesById(
        chat.chat_id, tokenEmployee, limit, offset, {
          markRead: false, beforeId: offset > 0 ? messagesRef.current.find(m => Number(m.id) > 0)?.id : null,
        },
      );
      if (activeSessionRef.current !== requestedSession) return;
      setMessages((prev) => mergeArrays(data?.messages?.reverse() || [], prev));
      setHasMore(data?.has_more ?? false);
      if (Array.isArray(data?.participants)) setChatParticipants(data.participants);
      if (data?.type_chat) setChatMeta({ sessionKey: requestedSession, chat: { type_chat: data.type_chat, is_dm: data.is_dm === true, display_name: data.display_name || chat?.display_name, event_id: data.event_id ?? null } });
    } catch (error) {
      console.error('Ошибка загрузки старых сообщений:', error);
    } finally {
      if (activeSessionRef.current === requestedSession) setLoading(false);
    }
  };

  useEffect(() => {
    if (!chat?.chat_id) return undefined;
    setMessages([]);
    setChatParticipants([]);
    setChatMeta(null);
    setLoading(false);
    setHasMore(true);
    setInitialLoad(true);
    setShowJump(false);
    setFirstUnreadId(null);
    msgNodes.current.clear();
    pendingReadRef.current.clear();

    let cancelled = false;
    (async () => {
      try {
        // anchor=unread — сервер отдаёт окно, начинающееся с границы
        // непрочитанного. markRead=false — отмечаем сами, по факту показа.
        const data = await APIGetChatMessagesById(
          chat.chat_id, tokenEmployee, 30, 0, { anchor: 'unread', markRead: false },
        );
        if (cancelled) return;
        setMessages(data?.messages?.reverse() || []);
        setHasMore(data?.has_more ?? false);
        setFirstUnreadId(data?.first_unread_id ?? null);
        if (Array.isArray(data?.participants)) setChatParticipants(data.participants);
        if (data?.type_chat) setChatMeta({ sessionKey, chat: { type_chat: data.type_chat, is_dm: data.is_dm === true, display_name: data.display_name || chat?.display_name, event_id: data.event_id ?? null } });
        setInitialLoad(false);
      } catch (error) {
        console.error('Ошибка загрузки сообщений:', error);
        if (!cancelled) setInitialLoad(false);
      }
    })();

    return () => { cancelled = true; };
  }, [sessionKey]);

  // Первая отрисовка: открываемся на границе непрочитанного, а если её нет —
  // в самом низу, как обычно.
  useEffect(() => {
    if (!initialLoad || messages.length === 0) return undefined;
    const t = setTimeout(() => {
      const divider = unreadDividerRef.current;
      if (divider) divider.scrollIntoView({ block: 'center' });
      else scrollToBottom(false);
      setInitialLoad(false);
    }, 100);
    return () => clearTimeout(t);
  }, [messages.length, initialLoad]);

  useEffect(() => {
    if (!initialLoad && prevScrollHeight > 0 && messagesContainerRef.current) {
      const newScrollHeight = messagesContainerRef.current.scrollHeight;
      messagesContainerRef.current.scrollTop = newScrollHeight - prevScrollHeight;
      setPrevScrollHeight(0);
    }
  }, [messages.length, initialLoad]);

  // ── WebSocket ──────────────────────────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    let connection = null;
    const requestedSession = sessionKey;
    const isCurrent = () => !cancelled && activeSessionRef.current === requestedSession;
    if (chat?.chat_id) {
      if (socketRef.current) {
        socketRef.current.close();
        socketRef.current = null;
      }

      const { close, isConnected } = createWebSocketConnection(
        chat?.chat_id,
        tokenEmployee,
        (data) => {
          if (!isCurrent()) return;
          // Кто-то вошёл или вышел — точка и подпись в шапке меняются сразу.
          if (data?.type === 'presence') {
            applyPresenceEvent(data?.data);
            return;
          }

          // Кто-то прочитал сообщения — у автора синеют галочки.
          if (data?.type === 'message_read') {
            const reader = data?.data?.reader;
            const itsMe = reader?.kind === 'employee'
              && String(reader?.id) === String(account?.id);
            if (itsMe) return;
            const ids = new Set(data?.data?.message_ids || []);
            if (ids.size === 0) return;
            setMessages((prev) => prev.map((m) => (
              ids.has(m.id) ? { ...m, views_count: (m.views_count || 0) + 1 } : m
            )));
            setReadTick((v) => v + 1);
            return;
          }

          // Закрепили или открепили — полоска общая для всех участников.
          if (data?.type === 'message_pin') {
            const mid = data?.data?.message_id;
            const isPinned = !!data?.data?.is_pinned;
            if (mid) {
              setMessages((prev) => prev.map((m) => (
                String(m?.id) === String(mid) ? { ...m, is_pinned: isPinned } : m
              )));
            }
            setPinnedIndex(0);
            reloadPinned();
            return;
          }

          const _mess = data?.data?.message;
          const action = data?.data?.action;
          if (!_mess) return;
          if (!_mess.attachments) _mess.attachments = [];

          if (action === 'updated') {
            setMessages((prevItems) => prevItems.map((item) => (item?.id === _mess.id ? _mess : item)));
            // ВАЖНО: правка старого сообщения не делает его последним в
            // списке чатов. Раньше строка чата подменялась любым обновлённым
            // сообщением — отредактируешь позавчерашнее, а в списке оно
            // всплывает как свежее.
            setChats((prevItems) => prevItems.map((item) => {
              if (item?.chat_id !== _mess?.chat_id) return item;
              if (!isLastMessage(item, _mess)) return item;
              return { ...item, last_message: lastMessageOf(_mess) };
            }));
          } else if (action === 'deleted') {
            setPinned(prev => prev.filter(m => m?.id !== _mess?.id));
            setMessages((prevItems) => {
              const filtered = prevItems.filter((item) => item?.id !== _mess?.id);
              const lastMsg = filtered[filtered.length - 1];
              setChats((prevChats) => prevChats.map((item) => (
                item?.chat_id === _mess?.chat_id
                  ? { ...item, last_message: lastMsg ? lastMessageOf(lastMsg) : null }
                  : item
              )));
              return filtered;
            });
          } else if (action === 'user_added') {
            // состав участников подтянется при следующей загрузке
          } else {
            setMessages((prev) => {
              if (prev.some((m) => m?.id === _mess.id)) return prev;
              // HTTP and WebSocket may arrive in either order. Reconcile the
              // visible optimistic message by its idempotency key, without motion.
              return upsertMessage(prev, _mess);
            });
            setChats((prevItems) => prevItems.map((item) => (
              item?.chat_id === _mess.chat_id
                ? { ...item, last_message: lastMessageOf(_mess) }
                : item
            )));
          }
        },
        (status) => {
          if (!isCurrent()) return;
          setWsConnectionStatus(status);
          if (status) {
            setWsFatalCode(null);
            // Пока связи не было, люди входили и выходили — событий мы не
            // видели. После переподключения статусы перечитываем целиком.
            reloadPresence();
          }
        },
        () => { if (isCurrent()) loadMessages(0); },
        // Сервер отказал осознанно (4001 — токен, 4003 — нет доступа):
        // переподключение не поможет, и говорить «восстанавливается»
        // было бы враньём.
        (code) => { if (isCurrent()) setWsFatalCode(code); },
      );

      connection = { close, isConnected };
      socketRef.current = connection;
    }

    return () => {
      cancelled = true;
      connection?.close();
      if (socketRef.current === connection) socketRef.current = null;
    };
  }, [chat?.chat_id, tokenEmployee, account?.id]);

  // ── Прокрутка ──────────────────────────────────────────────────────
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    // Контейнер перевёрнут (column-reverse): «низ» ленты — это scrollTop 0.
    setShowJump(Math.abs(scrollTop) > 240);
    if ((scrollTop * -1) >= (scrollHeight - clientHeight) - 100 && !loading && hasMore && !initialLoad) {
      loadMoreMessages();
    }
  };

  const loadMoreMessages = async () => {
    if (loading || !hasMore) return;
    await loadMessages(messages.length);
  };

  const scrollToBottom = (smooth = true) => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTo({ top: 0, behavior: smooth ? 'smooth' : 'auto' });
    else messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  };

  // Кнопка «вниз»: и уводит в конец ленты, и гасит остаток непрочитанного —
  // иначе счётчик пришлось бы вычитывать прокруткой через весь чат.
  const jumpDown = async () => {
    scrollToBottom();
    if (unreadInView === 0) return;
    setMessages((prev) => prev.map((m) => (needsRead(m) ? { ...m, is_read: true } : m)));
    pendingReadRef.current.clear();
    try {
      await APIMarkChatReadAll(chat.chat_id, tokenEmployee);
    } finally {
      Promise.resolve(markAsRead?.(chat.chat_id)).catch(() => {});
    }
  };

  // ── Отправка ───────────────────────────────────────────────────────
  const handleSendMessage = async (mentions = []) => {
    // В режиме правки та же кнопка сохраняет изменения, а не шлёт новое
    if (editing) { await submitEdit(); return; }
    if (attachmentQueue.job || attachments.length) {
      try {
        return await attachmentQueue.submit({ content: textAreaValue, files: attachments, mentions, replyTo });
      } catch (error) {
        setActionError(error.message);
        return false;
      }
    }
    if (attachments.length === 0 && !textAreaValue.trim()) return;

    const requestedSession = sessionKey;
    const text = textAreaValue;
    const filesToSend = attachments;
    const replySnapshot = replyTo;

    // Clear the composer and show the message immediately in the feed.
    setTextAreaValue('');
    setAttachments([]);
    setReplyTo(null);

    const tmpId = nextTmpId();
    const clientUid = newClientUid();
    const optimistic = {
      client_uid: clientUid,
      id: tmpId,
      chat_id: chat.chat_id,
      content: text,
      date_create: new Date().toISOString(),
      type_sender: 'employee',
      attachments: [],
      reply_to: replySnapshot
        ? {
            sender_name: personName(replySnapshot?.sender, ''),
            content: replySnapshot?.content,
          }
        : null,
      sender: { id: account?.id, name: account?.name, last_name: account?.last_name, full_name: personName(account) },
      mentions,
      views_count: 0,
      _pending: true,
    };

    setMessages(prev => [...prev, optimistic]);
    scrollToBottom(false);

    try {
      const formData = new FormData();
      formData.append('content', text);
      formData.append('client_uid', clientUid);
      filesToSend.forEach((file) => formData.append('attachments', file));
      if (replySnapshot?.id) formData.append('reply_to_id', replySnapshot.id);
      if (Array.isArray(mentions) && mentions.length > 0) {
        formData.append('mentions', JSON.stringify(mentions.map((m) => ({ kind: m.kind, id: m.id }))));
      }

      const sent = await APISendMessage(chat.chat_id, formData, tokenEmployee);

      if (sent && sent.id) {
        try {
          track('frontend.chat.message_sent', {
            title: `Отправил сообщение в чат #${chat.chat_id}`,
            meta: {
              chat_id: chat.chat_id,
              message_id: sent.id,
              has_attachments: (sent.attachments || []).length > 0,
              is_reply: !!replySnapshot?.id,
            },
          });
        } catch (e) { /* трекер не должен ломать отправку */ }

        if (!sent.attachments) sent.attachments = [];

        if (activeSessionRef.current !== requestedSession) return true;
        setMessages(prev => upsertMessage(prev, sent));
        setChats((prevItems) => prevItems.map((item) => (
          item?.chat_id === chat.chat_id
            ? { ...item, last_message: lastMessageOf(sent) }
            : item
        )));
      } else {
        throw new Error('Сервер не вернул сообщение');
      }
      return true;
    } catch (error) {
      console.error('Ошибка при отправке сообщения:', error);
      if (activeSessionRef.current !== requestedSession) return false;
      // Restore the draft if this is still the same chat.
      setMessages((prev) => prev.filter((m) => m?.id !== tmpId));
      setTextAreaValue((cur) => (cur ? cur : text));
      setAttachments(filesToSend);
      setReplyTo(replySnapshot);
      return false;
    }
  };

  const handleSendVoice = async (blob, meta, { onProgress, signal } = {}) => {
    const sent = await APISendVoiceMessage(
      chat.chat_id,
      blob,
      { duration: meta?.duration, waveform: meta?.waveform, replyToId: replyTo?.id },
      tokenEmployee,
      onProgress,
      signal,
    );
    if (sent && sent.id) {
      if (!sent.attachments) sent.attachments = [];
      setMessages((prev) => (prev.some((m) => m?.id === sent.id) ? prev : [...prev, sent]));
      setChats((prevItems) => prevItems.map((item) => (
        item?.chat_id === chat.chat_id
          ? { ...item, last_message: { ...lastMessageOf(sent), content: '🎤 Голосовое сообщение' } }
          : item
      )));
      setReplyTo(null);
      scrollToBottom();
    }
    return sent;
  };

  // ── Действия над сообщением ────────────────────────────────────────
  const canEditMessage = (m) => {
    if (!m || m.type_sender === 'system' || !isMine(m)) return false;
    if (isTmp(m.id)) return false;   // ещё не долетело до сервера
    // Окно правки — сутки, как на сервере. Показывать пункт, который вернёт
    // 409, смысла нет.
    const age = Date.now() - new Date(m.date_create).getTime();
    return age < 24 * 60 * 60 * 1000;
  };

  const canDeleteMessage = (m) => {
    if (!m || isTmp(m.id)) return false;
    const isBoss = account?.role === 'administrator';
    return isBoss || (m.type_sender !== 'system' && isMine(m));
  };

  const handleDeleteMessage = async (m) => {
    if (!canDeleteMessage(m)) return;
    const requestedSession = sessionKey;
    setMessages(prev => prev.filter(x => x?.id !== m.id));
    let res;
    try { res = await APIDeleteMessage(chat.chat_id, m.id, tokenEmployee); }
    catch (_) { res = null; }
    if (activeSessionRef.current !== requestedSession) return;
    if (res?.status !== 'ok') {
      // Restore only this message: a complete snapshot would discard messages
      // received while the delete request was in flight.
      setMessages(prev => prev.some(x => x?.id === m.id) ? prev : [...prev, m]);
      setActionError(res?.error || res?.detail || 'Не удалось удалить сообщение');
    } else {
      setPinned(prev => prev.filter(x => x?.id !== m.id));
    }
  };

  const handleReact = async (m, emoji) => {
    const res = await APIToggleMessageReaction(chat.chat_id, m.id, emoji, tokenEmployee);
    if (res?.status === 'ok' && res?.message) {
      setMessages((prev) => prev.map((x) => (x?.id === res.message.id ? res.message : x)));
    } else if (res?.error) {
      setActionError(res.error);
    }
  };

  const openMenu = (anchor, message) => {
    setMenu({ anchor, message });
    // Просмотры подтягиваем сразу: в подвале меню сидит строка «Прочитали N
    // из M», и ради неё не хочется заставлять человека делать лишний клик.
    setViewsData(null);
    if (message && !isTmp(message.id) && message.type_sender !== 'system') {
      setViewsLoading(true);
      APIGetMessageViews(chat.chat_id, message.id, tokenEmployee)
        .then((d) => { if (d?.status === 'ok') setViewsData(d); })
        .finally(() => setViewsLoading(false));
    }
  };

  const openViews = (message) => {
    setViewsOpen(true);
    if (!viewsData && message) {
      setViewsLoading(true);
      APIGetMessageViews(chat.chat_id, message.id, tokenEmployee)
        .then((d) => { if (d?.status === 'ok') setViewsData(d); })
        .finally(() => setViewsLoading(false));
    }
  };

  const startEdit = (m) => {
    if (attachmentQueue.job) return;
    setEditing(m);
    setReplyTo(null);
    setTextAreaValue(m.content || '');
  };

  const cancelEdit = () => {
    setEditing(null);
    setTextAreaValue('');
  };

  const submitEdit = async () => {
    const m = editing;
    const content = textAreaValue.trim();
    if (!m || !content) return;
    setEditing(null);
    setTextAreaValue('');
    // Показываем новый текст сразу, сервер подтвердит следом
    setMessages((prev) => prev.map((x) => (
      x?.id === m.id ? { ...x, content, is_edited: true } : x
    )));
    const res = await APIEditMessage(chat.chat_id, m.id, content, tokenEmployee);
    if (res?.id) {
      setMessages((prev) => prev.map((x) => (x?.id === res.id ? res : x)));
      // Строку в списке чатов трогаем, только если правили ПОСЛЕДНЕЕ сообщение
      setChats((prevItems) => prevItems.map((item) => {
        if (item?.chat_id !== chat.chat_id) return item;
        if (!isLastMessage(item, res)) return item;
        return { ...item, last_message: lastMessageOf(res) };
      }));
    } else {
      // Откат: возвращаем прежний текст и сам режим правки
      setMessages((prev) => prev.map((x) => (x?.id === m.id ? m : x)));
      setEditing(m);
      setTextAreaValue(content);
      setActionError(res?.detail || res?.error || 'Не удалось изменить сообщение');
    }
  };

  const handleAttachFiles = (files) => setAttachments(files);
  const handleUserAdded = () => { /* состав обновится при следующей загрузке */ };

  const unreadInView = useMemo(
    () => messages.filter(needsRead).length,
    [messages, account?.id],
  );

  // Последнее СВОЁ сообщение — про него и рассказывает полоска прочтений.
  // Временные (ещё не долетевшие) и системные не в счёт: у них нет id на
  // сервере, спрашивать о них некого.
  const lastOwnMessageId = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (!m || m.type_sender === 'system') continue;
      if (!isMine(m)) continue;
      return isTmp(m.id) ? null : m.id;
    }
    return null;
  }, [messages, account?.id]);

  // ── Раскладка: секции по дням, внутри — серии сообщений ────────────
  const sections = useMemo(() => {
    const out = [];
    let prev = null;

    const sameAuthorAs = (a, b) => {
      if (!a || !b) return false;
      if (a.type_sender === 'system' || b.type_sender === 'system') return false;
      if (String(a?.sender?.id ?? '') !== String(b?.sender?.id ?? '')) return false;
      if ((a?.sender?.kind || 'employee') !== (b?.sender?.kind || 'employee')) return false;
      const dt = Math.abs(new Date(b.date_create) - new Date(a.date_create));
      return dt <= GROUP_WINDOW_MS;
    };
    const sameDay = (a, b) => a && b && dayKey(a.date_create) === dayKey(b.date_create);

    messages.forEach((m, i) => {
      const next = messages[i + 1];
      const isSystem = m?.type_sender === 'system';
      const key = dayKey(m.date_create);

      if (out.length === 0 || out[out.length - 1].key !== key) {
        out.push({ key, label: dayLabel(m.date_create), items: [] });
      }

      const withPrev = sameAuthorAs(prev, m) && sameDay(prev, m);
      const withNext = sameAuthorAs(m, next) && sameDay(m, next);

      let groupPos = 'single';
      if (withPrev && withNext) groupPos = 'middle';
      else if (withPrev) groupPos = 'last';
      else if (withNext) groupPos = 'first';

      out[out.length - 1].items.push({
        key: `msg-${m.client_uid || m.id}`,
        message: m,
        isYou: isMine(m),
        isSystem,
        groupPos,
        showAvatar: !isSystem && (groupPos === 'last' || groupPos === 'single'),
        showName: !isSystem && isGroup && (groupPos === 'first' || groupPos === 'single'),
        // Разделитель «Непрочитанные» ставим ПЕРЕД первым непрочитанным
        unreadMark: firstUnreadId != null && m.id === firstUnreadId,
      });

      prev = m;
    });
    return out;
  }, [messages, account?.id, isGroup, firstUnreadId]);

  // ── Заглушка, когда чат не выбран ──────────────────────────────────
  if (!chat) {
    return (
      <Sheet
        sx={(theme) => ({
          // Высоту задаёт страница (см. useFillHeight в MessagesPage)
        height: '100%',
        minHeight: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          ...canvasBackground(theme),
        })}
      >
        <Box
          component={motion.div}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: MOTION.slow, ease: MOTION.ease }}
          sx={{ textAlign: 'center', px: 3, maxWidth: 360 }}
        >
          <Box
            sx={{
              width: 88, height: 88, mx: 'auto', mb: 2,
              borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'linear-gradient(135deg, rgba(99,102,241,0.18) 0%, rgba(124,58,237,0.14) 100%)',
            }}
          >
            <ForumRoundedIcon sx={{ fontSize: 40, color: 'primary.400' }} />
          </Box>
          <Typography level="title-md" sx={{ fontWeight: 700, mb: 0.5 }}>
            Выберите чат
          </Typography>
          <Typography level="body-sm" sx={{ color: 'text.tertiary' }}>
            Слева — переписки по событиям и заявкам. Откройте любую, чтобы продолжить разговор.
          </Typography>
        </Box>
      </Sheet>
    );
  }

  const renderItem = (row) => (
    // Fragment с ключом, а не обёртка: лишний Box стал бы элементом флекса
    // и сломал бы расстояния между сообщениями в серии.
    <Fragment key={row.key}>
        {row.unreadMark && (
          <Box ref={unreadDividerRef} sx={(theme) => unreadDivider(theme)}>
            Непрочитанные сообщения
          </Box>
        )}

        {row.isSystem ? (
          <Box
            data-mid={isTmp(row.message?.id) ? undefined : row.message?.id}
            ref={bindMsgNode(row.message?.id)}
            sx={{ display: 'flex', justifyContent: 'center', my: 0.75 }}
          >
            <ChatBubble
              message={row.message}
              isYou={false}
              onContextMenu={account?.role === 'administrator' ? openMenu : undefined}
              onReact={handleReact}
            />
          </Box>
        ) : (
          <Stack
            data-mid={isTmp(row.message?.id) ? undefined : row.message?.id}
            ref={bindMsgNode(row.message?.id)}
            direction="row"
            spacing={1}
            onClickCapture={event => mediaArchive.capture(event, row.message)}
            sx={{
              ...(mediaArchive.selected.has(String(row.message?.id)) ? {backgroundColor: 'primary.softBg', borderRadius: '12px'} : {}),
              flexDirection: row.isYou ? 'row-reverse' : 'row',
              alignItems: 'flex-end',
              minWidth: 0,
              maxWidth: '100%',
              mt: row.groupPos === 'first' || row.groupPos === 'single' ? 0.75 : 0,
              // Подсветка после перехода по закреплённому или по ответу.
              // Мягкая заливка на всю строку, а не рамка вокруг пузыря:
              // глаз ловит её боковым зрением и сразу понимает, куда попал.
              ...(String(row.message?.id) === String(flashId)
                ? {
                    borderRadius: '14px',
                    animation: 'chatFlash 1.7s ease-out 1',
                    '@keyframes chatFlash': {
                      '0%':   { backgroundColor: 'rgba(99,102,241,0.26)' },
                      '60%':  { backgroundColor: 'rgba(99,102,241,0.16)' },
                      '100%': { backgroundColor: 'rgba(99,102,241,0)' },
                    },
                  }
                : null),
            }}
          >
            {!row.isYou && (
              <Box sx={{ width: CHAT.avatarSm, flexShrink: 0 }}>
                {row.showAvatar && (
                  <AvatarWithStatus size="sm" name={personName(row.message?.sender)} person={row.message?.sender} />
                )}
              </Box>
            )}
            <ChatArchiveCheckbox message={row.message} archive={mediaArchive}/>
            <ChatBubble
              message={row.message}
              isYou={row.isYou}
              onReply={setReplyTo}
              onContextMenu={openMenu}
              onReact={handleReact}
              showName={row.showName}
              groupPos={row.groupPos}
            />
          </Stack>
        )}
    </Fragment>
  );

  return (
    <Sheet
      sx={(theme) => ({
        // Высоту задаёт страница (см. useFillHeight в MessagesPage)
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflowX: 'hidden',
        // Само полотно не прокручивается НИКОГДА: шапка и поле ввода — это
        // flex-края, а двигаться должна только лента между ними.
        overflowY: 'hidden',
        overscrollBehavior: 'none',
        maxWidth: '100%',
        position: 'relative',
        ...canvasBackground(theme),
      })}
    >
      <MessagesPaneHeader
        chat={currentChat}
        wsConnected={wsConnectionStatus}
        wsFatalCode={wsFatalCode}
        presence={presence}
        participants={chatParticipants.length ? chatParticipants : (chat?.participants || [])}
        searchParams={searchParams}
        setSelectedChat={setSelectedChat}
        setSearchParams={setSearchParams}
        onUserAdded={handleUserAdded}
      />

      <ChatArchiveSelection archive={mediaArchive}/>

      {/* Полоска закреплённого — сразу под шапкой, как и ожидается:
          это продолжение шапки, а не часть ленты, и прокручиваться вместе
          с сообщениями она не должна. */}
      <PinnedBar
        pinned={pinned}
        index={pinnedIndex}
        busy={jumping}
        onJump={jumpToMessage}
        onCycle={() => setPinnedIndex((i) => (pinned.length ? (i + 1) % pinned.length : 0))}
        onOpenList={() => setPinnedListOpen(true)}
      />

      <Box
        ref={messagesContainerRef}
        onScroll={handleScroll}
        sx={(theme) => ({
          display: 'flex',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          px: { xs: 1.5, md: 3 },
          pt: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          flexDirection: 'column-reverse',
          // Прокрутка не должна «перетекать» наружу: дойдя до края ленты,
          // палец тянул за собой всё окно и появлялся резиновый отскок с
          // пустыми полями над шапкой и под полем ввода.
          overscrollBehavior: 'contain',
          WebkitOverflowScrolling: 'touch',
          ...thinScrollbar(theme),
        })}
      >
        <Stack spacing={0.375} sx={{ justifyContent: 'flex-end', pb: 1 }}>
          {sections.map((section) => (
            // Секция одного дня. Липкость чипа ограничена ЭТОЙ секцией:
            // дойдя до её конца, чип уходит вверх, а на его место встаёт чип
            // следующего дня. Ровно один видимый чип, без наслоения.
            <Box key={section.key} sx={{ position: 'relative' }}>
              <Box sx={dayChipWrap}>
                <Box
                  component={motion.div}
                  initial={{ opacity: 0, scale: 0.94 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: MOTION.base }}
                  sx={dateChip}
                >
                  {section.label}
                </Box>
              </Box>
              <Stack spacing={0.375}>
                {section.items.map(renderItem)}
              </Stack>
            </Box>
          ))}
          <div ref={messagesEndRef} />
        </Stack>

        {/* Спиннер догрузки — ПОСЛЕ ленты в разметке, потому что контейнер
            перевёрнут: так он оказывается сверху, где и подгружается история. */}
        {loading && (
          <Stack alignItems="center" sx={{ py: 1.5 }}>
            <CircularProgress size="sm" />
          </Stack>
        )}
      </Box>

      {/* Ошибка действия над сообщением */}
      <AnimatePresence>
        {actionError && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            onClick={() => setActionError('')}
            sx={{
              position: 'absolute', bottom: 96, left: '50%', transform: 'translateX(-50%)',
              zIndex: 7, cursor: 'pointer',
              px: 2, py: 1, borderRadius: '12px', maxWidth: '80%',
              backgroundColor: 'danger.500', color: '#fff',
              boxShadow: 'md', fontSize: 13, fontWeight: 600,
            }}
          >
            {actionError}
          </Box>
        )}
      </AnimatePresence>

      {/* Кнопка «вниз» со счётчиком непрочитанного */}
      <AnimatePresence>
        {(showJump || unreadInView > 0) && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, scale: 0.7, y: 8 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.7, y: 8 }}
            transition={MOTION.spring}
            sx={{ position: 'absolute', right: 20, bottom: 104, zIndex: 5 }}
          >
            <Box sx={{ position: 'relative' }}>
              <IconButton
                variant="solid"
                onClick={jumpDown}
                title={unreadInView > 0 ? 'Прочитать всё и перейти вниз' : 'Вниз'}
                sx={(theme) => ({
                  borderRadius: '50%',
                  '--IconButton-size': '40px',
                  backgroundColor: 'background.surface',
                  boxShadow: 'md',
                  ...iconColor(theme.palette.mode === 'dark'
                    ? 'rgba(255,255,255,0.7)' : 'rgba(71,84,103,0.78)'),
                  '&:hover': {
                    backgroundColor: 'background.level1',
                    ...iconColor('var(--joy-palette-primary-500)'),
                  },
                })}
              >
                <KeyboardArrowDownRoundedIcon />
              </IconButton>
              {unreadInView > 0 && (
                <Box
                  sx={{
                    position: 'absolute', top: -6, right: -4,
                    minWidth: 18, height: 18, px: 0.5,
                    borderRadius: '9px',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    backgroundColor: 'primary.500', color: '#fff',
                    fontSize: 10.5, fontWeight: 800, lineHeight: 1,
                    pointerEvents: 'none',
                    boxShadow: '0 2px 6px rgba(79,70,229,0.45)',
                  }}
                >
                  {unreadInView > 99 ? '99+' : unreadInView}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </AnimatePresence>

      {/* «Прочитали…» — между лентой и полем ввода: смотрят туда же, куда и
          после отправки, и полоска не наезжает на последний пузырь. */}
      <ReadReceiptsBar
        chatId={chat?.chat_id}
        token={tokenEmployee}
        messageId={lastOwnMessageId}
        refreshKey={readTick}
      />

      <ChatQueueStatus
        job={attachmentQueue.job}
        onRetry={() => attachmentQueue.submit()}
        onDiscard={attachmentQueue.discard}
      />
      <MessageInput
        key={`${sessionKey}:${attachmentReset}`}
        attachments={attachments}
        disabled={Boolean(attachmentQueue.job)}
        textAreaValue={textAreaValue}
        setTextAreaValue={setTextAreaValue}
        onSubmit={handleSendMessage}
        onSendVoice={handleSendVoice}
        onAttach={handleAttachFiles}
        participants={chatParticipants.length ? chatParticipants : (chat?.participants || [])}
        replyTo={replyTo}
        onCancelReply={() => setReplyTo(null)}
        editing={editing}
        onCancelEdit={cancelEdit}
        // Клавиатура выехала — полотно стало ниже. Досматриваем ленту вниз,
        // иначе последнее сообщение остаётся за новым нижним краем и кажется,
        // что чат «уехал».
        onKeyboardOpen={() => scrollToBottom(false)}
      />

      {/* Контекстное меню сообщения */}
      {menu && (
        <MessageContextMenu
          anchor={menu.anchor}
          message={menu.message}
          canEdit={canEditMessage(menu.message)}
          canDelete={canDeleteMessage(menu.message)}
          views={viewsData}
          viewsLoading={viewsLoading}
          onOpenViews={openViews}
          onReply={setReplyTo}
          onEdit={startEdit}
          onDelete={handleDeleteMessage}
          onReact={(emoji) => handleReact(menu.message, emoji)}
          onTogglePin={handleTogglePinMessage}
          onSelectFiles={hasArchiveMedia(menu.message) ? mediaArchive.toggle : undefined}
          onClose={() => setMenu(null)}
        />
      )}

      {/* Кто прочитал и кто отреагировал */}
      <MessageViewsModal
        open={viewsOpen}
        data={viewsData}
        loading={viewsLoading}
        onClose={() => setViewsOpen(false)}
      />

      {/* Все закреплённые списком */}
      <ModalPinnedList
        open={pinnedListOpen}
        pinned={pinned}
        onClose={() => setPinnedListOpen(false)}
        onJump={jumpToMessage}
        onUnpin={(id) => {
          const inFeed = messagesRef.current.find((m) => String(m?.id) === String(id));
          handleTogglePinMessage(inFeed || { id, is_pinned: true });
        }}
      />

      {/* Упор в лимит закреплённых и прочие отказы — тихой плашкой снизу:
          модалка ради одной строки тут была бы тяжелее самой проблемы. */}
      <AnimatePresence>
        {pinError && (
          <Box
            component={motion.div}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: MOTION.fast, ease: MOTION.ease }}
            onClick={() => setPinError(null)}
            sx={{
              position: 'absolute', left: 0, right: 0, bottom: 84,
              mx: 'auto', width: 'fit-content', maxWidth: '86%',
              px: 1.75, py: 1, borderRadius: '14px',
              backgroundColor: 'warning.softBg',
              border: '1px solid', borderColor: 'warning.300',
              boxShadow: 'md', cursor: 'pointer', zIndex: 8,
            }}
          >
            <Typography level="body-xs" sx={{ fontWeight: 600, color: 'warning.700' }}>
              {pinError}
            </Typography>
          </Box>
        )}
      </AnimatePresence>

    </Sheet>
  );
}

/** Строка «последнее сообщение» для списка чатов. */
function lastMessageOf(m) {
  if (!m) return null;
  return {
    id: m.id,
    chat_id: m.chat_id,
    content: m.content,
    type_sender: m.type_sender,
    date_create: m.date_create,
    sender: m.sender,
    attachments: m.attachments || [],
  };
}

/**
 * Это сообщение и есть последнее в строке чата?
 *
 * Сравниваем по id, а если строка пришла из старого ответа без id — по дате.
 * Нужно, чтобы правка позавчерашнего сообщения не всплывала в списке чатов
 * как свежая.
 */
function isLastMessage(chatRow, m) {
  const last = chatRow?.last_message;
  if (!last) return true;
  if (last.id != null && m?.id != null) return String(last.id) === String(m.id);
  if (last.date_create && m?.date_create) {
    return new Date(m.date_create).getTime() >= new Date(last.date_create).getTime();
  }
  return true;
}
